import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, inject, Injector, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, TemplateRef, ViewChild, ViewChildren, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { catchError, finalize, switchMap, map, tap, Subscription, filter, Observable, of } from 'rxjs';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { CommonConfirmationDialog } from '../../models/user-preferences.model';
import {
  WorkPackageView,
  CategorizedViews,
  GridColumn,
  ViewFilterRule,
  ViewFiltersPayload,
  ViewDefinitionPayload,
  ViewVisibility,
  ViewCategory,
  ViewAccessLevel,
} from '../../models/work-package-view.model';
import {
  FilterOperatorDef,
  FilterSelection,
  isBetweenOperator,
  isMultiValueOperator,
  isNoValueOperator,
} from '../../models/filter.model';
import { GridColumnDef } from '../../models/work-order.model';
import { FilterOperator, ColumnType } from '../../services/views/views.enums';
import { RecordState, StatusID } from '../../enums/user';
import { WOStatusDraftOrPublished } from '../../enums/workOrder';
import { REPORTING_VIEWS_SERVICE_TOKEN } from '../../services/reporting-views/reporting-views.token';
import { ReportingModule, toReportingModule } from '../../services/reporting-views/reporting-views.service';
import { UserPreferencesService } from '../../services/user-preferences/user-preferences.service';
import { ViewsFilterService } from '../../services/views/views-filter.service';
import { UserListCacheService, UserOption } from '../../services/user/user-list-cache.service';
import { UtilsService } from '../../services/utils/utils.service';
import { LocalStorageService } from '../../services/local-storage/localstorage.service';
import { CentralizedTagsService } from '../../services/centralized-tags/centralized-tags.service';
import { CustomFieldsService } from '../../services/custom-fields/custom-fields.service';
import { withCustomFieldOptions } from '../../services/views/list-view-filters';
import { EntityType } from '../../enums/entity-type.enum';
import { PermissionService } from '../../../core/services/permission.service';
import { CreateViewDialogComponent } from './create-view-dialog/create-view-dialog.component';
import { ViewsListDialogComponent } from './views-list-dialog/views-list-dialog.component';

/**
 * Filter item for the inline filter builder.
 * Uses the same key structure as milestone tracker for future API compatibility.
 */
export interface FilterItem {
  column: GridColumn;
  operator: string;
  value: string | number; // single value; see valueTo/multiValues below for between/in-notin operators
  valueTo?: string; // second value for 'between' operator
  multiValues?: string[]; // for 'in'/'notin' operators
}

/**
 * One row in the compact view-switcher dropdown.
 * `hidden` is true when the view exists but is not currently an open tab
 * (i.e. "Hidden" on the All Views page) — surfaced with a badge so users see
 * every available view, not just the ones pinned as tabs.
 */
export interface ViewSwitcherItem {
  view: WorkPackageView;
  hidden: boolean;
}

/**
 * Generic Saved Views Component
 * Can be reused across different modules (Work Packages, Inspections, Actions, etc.)
 */
@Component({
  selector: 'app-saved-views',
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- pre-existing NgModule-declared component; standalone migration is tracked separately (COL-36/COL-37), out of scope for this hotfix
  standalone: false,
  templateUrl: './saved-views.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './saved-views.component.scss',
})
export class SavedViewsComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  private dialog = inject(MatDialog);
  private injector = inject(Injector);
  private router = inject(Router);
  private reportingViewsService = inject(REPORTING_VIEWS_SERVICE_TOKEN);
  private userPreferencesService = inject(UserPreferencesService);
  private viewsFilterService = inject(ViewsFilterService);
  private userListCacheService = inject(UserListCacheService);
  private utilsService = inject(UtilsService);
  private localStorageService = inject(LocalStorageService);
  private centralizedTagsService = inject(CentralizedTagsService);
  private customFieldsService = inject(CustomFieldsService);
  private permissionService = inject(PermissionService);
  private ngZone = inject(NgZone);
  @Input() enableViews = true;

  @Input() moduleType: 'work-package' | 'work-order' | 'scheduler' | 'inspection' | 'action' | 'booking' | 'contact' | 'eoi' =
    'work-package';
  @Input() allViewsRoute = '/work-packages/all-views'; // Route to "All Views" page

  /** Maps moduleType input to the URL module segment used by the API */
  get moduleSegment(): ReportingModule {
    return toReportingModule(this.moduleType);
  }
  @Input() availableColumns: GridColumn[] = []; // Fallback columns if API doesn't return any

  /**
   * True while the host is still fetching the options behind one of `availableColumns` — a lookup
   * column whose `lookupOptions` arrive from their own request.
   *
   * The filter button becomes a skeleton for that window. The column would otherwise be offered
   * with an empty dropdown behind it, which reads as "this company has no schedules" rather than
   * "the list has not arrived".
   */
  @Input() filterOptionsLoading = false;

  /**
   * Filters the Default view starts with when the user has none saved locally for it — e.g. Bookings'
   * "Confirmed" (status = Active), which its old chip bar applied whenever no filter was chosen.
   * Null (the default) leaves the Default view unfiltered, as every other module has it.
   */
  @Input() defaultViewFilters: ViewFiltersPayload | null = null;

  // ----- Grid tools carried on the filter row (opt-in; nothing renders unless a host passes them) -----
  /**
   * The host's search control, rendered as the filter row's search field. Owned by the host rather
   * than created here so its existing debounce, reset and query wiring keep working untouched.
   */
  @Input() searchControl?: UntypedFormControl;
  @Input() searchPlaceholder = 'Search';
  /** Grid columns for the Edit Columns button. Empty hides the button. */
  @Input() columnConfig: GridColumnDef[] = [];
  @Input() hiddenColumns: string[] = [];
  @Output() hiddenColumnsChange = new EventEmitter<string[]>();
  /**
   * Host-supplied controls projected onto the end of the tool cluster, after a divider — a grid's own
   * actions (Download, Import, …) belong on this strip rather than on a second row of their own.
   *
   * A slot, deliberately not a feature: the row renders whatever the host passes and knows nothing
   * about it, so no grid's actions leak into the shared component.
   */
  @Input() gridToolsExtra?: TemplateRef<unknown>;
  /** List of milestones for the filter milestone-selector dropdown (passed in from parent after data loads) */
  @Input() milestoneOptions: { id: number; name: string }[] = [];
  /** When true the inline filter row is hidden; a compact pill button toggles it instead. */
  @Input() compactMode = false;

  /** Whether the compact filter drawer is open */
  compactFilterExpanded = false;

  toggleCompactFilter(): void {
    this.compactFilterExpanded = !this.compactFilterExpanded;
  }

  get activeFilterCount(): number {
    return this.appliedFilters.length;
  }

  /**
   * The host's quick filters that are on, by label (Schedules: "Active", "My favourites" …). The
   * full-page filter bar lists them beside the filters; the pills themselves are projected into the
   * filter panel with `svQuickFilters`.
   */
  @Input() quickFilterLabels: string[] = [];

  /** How many filter chips the folded bar shows before "+N more" — one line, whatever is on. */
  private static readonly SUMMARY_CHIPS = 4;

  private static nextPanelId = 0;
  /** Unique per instance — a dashboard renders several of these on one page. */
  readonly filterPanelId = `sv-filter-panel-${SavedViewsComponent.nextPanelId++}`;

  private _filtersExpanded: boolean | null = null;

  /**
   * Full page: whether the filter panel under the one-line filter bar is open. Remembered per list
   * on this device; starts folded. (Widget mode has its own drawer — see compactFilterExpanded.)
   */
  get filtersExpanded(): boolean {
    if (this._filtersExpanded === null) {
      try {
        this._filtersExpanded = localStorage.getItem(this.filtersExpandedKey) === 'true';
      } catch {
        this._filtersExpanded = false;
      }
    }
    return this._filtersExpanded;
  }

  toggleFiltersExpanded(): void {
    this._filtersExpanded = !this.filtersExpanded;
    try {
      localStorage.setItem(this.filtersExpandedKey, String(this._filtersExpanded));
    } catch {
      // storage unavailable — the toggle still works for this visit
    }
  }

  private get filtersExpandedKey(): string {
    return `saved-views:filters-expanded:${this.moduleType}`;
  }

  /**
   * What the folded filter bar says: one chip per filter that is actually narrowing the list
   * ("Schedule Name · contains test"), then the host's quick filters, capped at SUMMARY_CHIPS with
   * the rest counted. A chip still waiting for its value filters nothing, so it is not counted.
   */
  get filterSummary(): { count: number; shown: { label: string; text: string }[]; more: number } {
    const items = [
      ...this.appliedFilters.filter((f) => this.isFilterEffective(f)).map((f) => this.summarizeFilter(f)),
      ...this.quickFilterLabels.map((text) => ({ label: '', text })),
    ];
    const shown = items.slice(0, SavedViewsComponent.SUMMARY_CHIPS);
    return { count: items.length, shown, more: items.length - shown.length };
  }

  /** Same test buildFiltersPayload applies before it sends a rule. */
  private isFilterEffective(filter: FilterItem): boolean {
    if (this.isNoValue(filter.operator)) return true;
    if (this.isMulti(filter.operator)) return !!filter.multiValues?.length;
    if (this.isBetween(filter.operator)) return !!filter.value || !!filter.valueTo;
    return !!filter.value;
  }

  private summarizeFilter(filter: FilterItem): { label: string; text: string } {
    const op = (this.getOperatorsForColumn(filter.column).find((o) => o.value === filter.operator)?.label ?? filter.operator).toLowerCase();
    const kind = this.isUserField(filter.column) ? 'user' : this.isLookupField(filter.column) ? 'lookup' : 'plain';
    const show = (v: unknown): string => SavedViewsComponent.readableDate(String(v ?? '')) || '…';
    // "Contains data" / "Does not contain data" say it all; the rest name their value(s).
    const value = this.isNoValue(filter.operator) ? ''
      : this.isMulti(filter.operator) ? this.multiValueSummary(filter, kind)
      : this.isBetween(filter.operator) ? `${show(filter.value)} – ${show(filter.valueTo)}`
      : show(filter.value);
    return { label: filter.column.headerText, text: value ? `${op} ${value}` : op };
  }

  /** "2026-09-20" → "20/09/2026", "2026-09-20T14:30" → "20/09/2026 14:30"; anything else as is. */
  private static readableDate(value: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}:\d{2}))?/.exec(value);
    return m ? `${m[3]}/${m[2]}/${m[1]}${m[4] ? ' ' + m[4] : ''}` : value;
  }

  /**
   * Flat, ordered list of views the compact (dashboard-widget) view switcher
   * offers in its dropdown: the local system views first (Default, plus
   * Archive/Draft for work orders), then the user's open views. Mirrors the
   * order of the full-page pill strip without the per-pill management chrome.
   */
  get switchableViews(): WorkPackageView[] {
    const sessionViews = this.visibleViews.filter((v) => !this.isDefaultView(v));
    return this.moduleType === 'work-order'
      ? [this.defaultView, this.archiveView, this.draftView, ...sessionViews]
      : [this.defaultView, ...sessionViews];
  }

  /** Compact dropdown handler — apply the picked view (no-op if already active). */
  onCompactViewChange(viewId: number): void {
    const view = this.switchableViews.find((v) => v.id === viewId);
    if (view && !this.isActiveView(view)) {
      this.applyView(view);
    }
  }

  // ========== COMPACT VIEW SWITCHER (searchable, all-views dropdown) ==========

  /** Content projected into the compact toolbar on the same row as the View
   *  switcher (e.g. the work-package Milestone filter). */
  @Input() toolbarExtra?: TemplateRef<unknown>;
  /** Total record count shown in the compact "View all N" link (null hides it). */
  @Input() totalCount: number | null = null;
  /** Emitted when the compact "View all" link is clicked — parent navigates to the full page. */
  @Output() viewAll = new EventEmitter<void>();
  /** Compact mode: show the list screens' Export button beside the filter icon. */
  @Input() showExport = false;
  /** Emitted when the compact Export button is clicked — the parent opens its export dialog. */
  @Output() exportData = new EventEmitter<void>();

  // ── Dashboard-view save affordance (compact mode only) ─────────────────────
  /**
   * Layout-scoped view ID to restore on init (compact/dashboard mode only).
   * When provided, overrides the module-level session's activeViewId so each
   * layout widget independently remembers its own selected view.
   */
  @Input() restoreViewId?: number;

  /** The widget has its own copy of the view — show the "Customised" chip. */
  @Input() dashboardViewBound = false;
  /** Bound tblDashboardView id, passed into the Edit View dialog so edits update it (not the grid view). */
  @Input() boundDashboardViewId: number | null = null;
  /** Resolved dashboard view (columns/filters/sort) — seeds the Edit View dialog so it shows the saved dashboard settings. */
  @Input() dashboardViewDefinition: WorkPackageView | null = null;
  /** Name the widget's dashboard view is saved under (passed to the Edit View dialog). */
  @Input() dashboardViewName?: string;
  /** Emitted with the dashboard-view id after an edit saves it, so the widget can bind to it. */
  @Output() dashboardViewSaved = new EventEmitter<number>();

  viewDropdownOpen = false;
  viewSearchText = '';
  isLoadingAllViews = false;
  /** True once the full view list (including hidden) has been fetched for the switcher. */
  private allViewsLoadedForSwitcher = false;
  /** All views offered by the switcher (visible first, then hidden), pre-search. */
  viewSwitcherItems: ViewSwitcherItem[] = [];
  /** `viewSwitcherItems` filtered by the search box — what the template renders. */
  filteredViewItems: ViewSwitcherItem[] = [];

  /** Active view name shown on the compact switcher trigger — "Default View" is shortened to "Default". */
  getActiveViewName(): string {
    const name = this.activeView?.name ?? 'Select view';
    return name === 'Default View' ? 'Default' : name;
  }

  /**
   * Open/close the compact view-switcher dropdown. On first open we lazily fetch
   * the full view list so hidden (not-in-tabs) views can be offered with a badge.
   */
  toggleViewDropdown(event?: Event): void {
    event?.stopPropagation();
    this.viewDropdownOpen = !this.viewDropdownOpen;
    if (this.viewDropdownOpen) {
      this.viewSearchText = '';
      this.rebuildViewItems();
      if (!this.allViewsLoadedForSwitcher) {
        this.loadAllViewsForSwitcher();
      }
    }
  }

  closeViewDropdown(): void {
    this.viewDropdownOpen = false;
    this.viewSearchText = '';
  }

  onViewSearchChange(): void {
    this.rebuildViewItems();
  }

  /** Apply the picked view (no-op if already active), then close the dropdown. */
  selectViewFromDropdown(item: ViewSwitcherItem): void {
    this.closeViewDropdown();
    if (!this.isActiveView(item.view)) {
      this.applyView(item.view);
    }
  }

  /**
   * Build the switcher list: visible (in-tabs / system) views first, then any
   * hidden views from the full list — then apply the current search filter.
   */
  private rebuildViewItems(): void {
    const visible: ViewSwitcherItem[] = this.switchableViews.map((v) => ({ view: v, hidden: false }));
    const visibleIds = new Set(this.switchableViews.map((v) => v.id));
    const hidden: ViewSwitcherItem[] = (this.views ?? [])
      .filter((v) => (v?.name?.trim()?.length ?? 0) > 0 && !visibleIds.has(v.id))
      .map((v) => ({ view: v, hidden: true }));

    this.viewSwitcherItems = [...visible, ...hidden];

    const term = this.viewSearchText.trim().toLowerCase();
    this.filteredViewItems = term
      ? this.viewSwitcherItems.filter((i) => i.view.name.toLowerCase().includes(term))
      : this.viewSwitcherItems;
  }

  /** Fetch every saved view so the switcher can list hidden ones with a badge. */
  private loadAllViewsForSwitcher(): void {
    this.isLoadingAllViews = true;
    this.subs.add(
      this.reportingViewsService
        .listViews(undefined, undefined, undefined, undefined, this.moduleSegment)
        .pipe(finalize(() => { this.isLoadingAllViews = false; }))
        .subscribe({
          next: (views) => {
            this.views = views;
            this.allViewsLoadedForSwitcher = true;
            this.categorizeViews();
            this.rebuildViewItems();
          },
          error: (err) => console.error('Failed to load views for switcher:', err),
        }),
    );
  }

  @Output() viewApplied = new EventEmitter<WorkPackageView>();
  @Output() viewApplying = new EventEmitter<void>();
  @Output() viewCleared = new EventEmitter<void>();
  @Output() columnsLoaded = new EventEmitter<GridColumn[]>();

  // Data from API
  views: WorkPackageView[] = [];
  standardColumns: GridColumn[] = [];
  customColumns: GridColumn[] = [];

  categorizedViews: CategorizedViews = {
    colobboProvided: [],
    adminPromoted: [],
    createdByMe: [],
    createdByOthers: [],
  };

  visibleViews: WorkPackageView[] = []; // Views displayed in the pills row (from sessions)
  activeView: WorkPackageView | null = null;
  currentUserId = 0;
  readonly MAX_USER_VIEWS = 10;

  // Frontend-only default view (id=0, always shown first, cannot be removed/reordered)
  private readonly DEFAULT_VIEW_ID = 0;
  private readonly ARCHIVE_VIEW_ID = -1;
  private readonly DRAFT_VIEW_ID = -2;

  readonly defaultView: WorkPackageView = {
    id: this.DEFAULT_VIEW_ID,
    name: 'Default View',
    description: 'Shows all records with all columns and no filters',
    ownerId: 0,
    ownerName: 'System',
    accessLevel: ViewAccessLevel.Everyone,
    category: ViewCategory.ColobboProvided,
    companyId: 0,
    projectId: 0,
    createdDate: '',
    modifiedDate: '',
    isDefault: true,
    columns: { standard: [], custom: [] },
    filters: { logic: 'AND', rules: [] },
  };

  // Work-order-only system views (local-only, never stored in DB, never removable)
  readonly archiveView: WorkPackageView = {
    id: this.ARCHIVE_VIEW_ID,
    name: 'Archive',
    description: 'Shows archived work orders',
    ownerId: 0,
    ownerName: 'System',
    accessLevel: ViewAccessLevel.Everyone,
    category: ViewCategory.ColobboProvided,
    companyId: 0,
    projectId: 0,
    createdDate: '',
    modifiedDate: '',
    isDefault: false,
    columns: { standard: [], custom: [] },
    filters: {
      logic: 'AND',
      rules: [
        { type: ColumnType.STANDARD, field: 'parentWorkOrderId', op: FilterOperator.EQUALS, value: '0' } as ViewFilterRule,
        { type: ColumnType.STANDARD, field: 'recordState', op: FilterOperator.EQUALS, value: RecordState.Archived } as ViewFilterRule,
      ],
    },
  };

  readonly draftView: WorkPackageView = {
    id: this.DRAFT_VIEW_ID,
    name: 'Draft',
    description: 'Shows draft work orders',
    ownerId: 0,
    ownerName: 'System',
    accessLevel: ViewAccessLevel.Everyone,
    category: ViewCategory.ColobboProvided,
    companyId: 0,
    projectId: 0,
    createdDate: '',
    modifiedDate: '',
    isDefault: false,
    columns: { standard: [], custom: [] },
    filters: {
      logic: 'AND',
      rules: [
        { type: ColumnType.STANDARD, field: 'parentWorkOrderId', op: FilterOperator.EQUALS, value: '0' } as ViewFilterRule,
        { type: ColumnType.STANDARD, field: 'recordState', op: FilterOperator.EQUALS, value: RecordState.Active } as ViewFilterRule,
        { type: ColumnType.STANDARD, field: 'status', op: FilterOperator.IN, values: [WOStatusDraftOrPublished.Draft] } as ViewFilterRule,
      ],
    },
  };

  // Loading & error states
  isLoading = true;
  hasError = false;

  // Filter builder properties
  appliedFilters: FilterItem[] = [];
  showFilterMenu = false;
  filterLogic: 'AND' | 'OR' = 'AND';
  filterSearchTerm = ''; // Search term for filtering columns in the menu
  userOptions: UserOption[] = []; // User list for dropdown filters
  stateOptions: string[] = []; // Australian state list for state dropdown filter
  /** Shared milestone ID applied to all milestone filters */
  sharedMilestoneId = 0;

  // Dropdown smart positioning: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'
  filterMenuPosition = 'bottom-right';
  userFilterDropdownPosition = 'bottom-left';

  @Output() filtersChanged = new EventEmitter<{
    filters: FilterSelection[];
    logic: 'AND' | 'OR';
    payload: ViewFiltersPayload;
    /** True when the user has unsaved filter changes on a named view — list should use filter payload instead of viewId */
    isDirty: boolean;
  }>();
  /** O(1) user ID → name lookup; rebuilt whenever userOptions loads */
  private userMap = new Map<number, string>();
  private _userCreatedViewsCount = 0;

  private filterValueDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly DEBOUNCE_TIME = 600;
  /** Snapshot of ViewFiltersPayload+logic at the time the view was last loaded/saved — used for dirty detection and revert */
  private _originalViewFilters: { filters: ViewFiltersPayload; logic: 'AND' | 'OR' } | null = null;
  /**
   * Saved rules whose column was not in `availableColumns` when the view was applied. The view is
   * applied the moment the API columns land — a tick before the host's own filter-only columns
   * (e.g. Created By picked by user id) reach this input — so a rule on one of those waits here for
   * its chip instead of being dropped from the row, and from the next save, while still filtering
   * the grid.
   */
  private unresolvedRules: ViewFilterRule[] = [];
  /** True when the user has made unsaved filter changes to a named view */
  isDirty = false;
  isSavingView = false;
  isOrderSaving = false;

  private _suppressFilterEmit = false;
  private subs = new Subscription();
  private viewsListDialogRef: MatDialogRef<ViewsListDialogComponent, WorkPackageView | { action: string }> | null = null;
  isApplyingView = false;

  // Tags filter support
  tagOptions: { id: string; name: string }[] = [];
  tagsLoaded = false;
  tagsLoading = false;

  /** Dropdown custom fields' options by field key, put on their columns so the filter row offers a picker. */
  private customFieldOptions = new Map<string, string[]>();
  private customFieldOptionsSub?: Subscription;

  /** Entity type whose custom fields each module's views offer; a module without custom fields is absent. */
  private static readonly CUSTOM_FIELD_ENTITY_TYPES: Partial<Record<string, EntityType>> = {
    'work-order': EntityType.WorkOrder,
    'work-package': EntityType.WorkPackage,
    action: EntityType.Action,
  };

  // Horizontal scroll for view pills
  @ViewChild('viewsScrollContainer') viewsScrollContainer!: ElementRef<HTMLElement>;
  canScrollLeft = false;
  canScrollRight = false;
  private scrollAmount = 200; // pixels to scroll per click
  private resizeObserver: ResizeObserver | null = null;

  ngOnInit(): void {
    this.loadUserContext();
    this.stateOptions = this.utilsService.getStateList();

    if (!this.enableViews) {
      this.isLoading = false;
      return;
    }

    this.loadUserList();
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.checkViewsScrollState();
    // Watch for container size changes (e.g. window resize, views added/removed)
    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        this.ngZone.run(() => this.checkViewsScrollState());
      });
      if (this.viewsScrollContainer?.nativeElement) {
        this.resizeObserver.observe(this.viewsScrollContainer.nativeElement);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The host builds its columns without the custom fields' dropdown options — put them back.
    if (changes['availableColumns']) {
      this.availableColumns = withCustomFieldOptions(this.availableColumns, this.customFieldOptions);
    }
    // When the parent updates availableColumns with enriched data (e.g. lookupOptions for
    // status/type dropdowns), refresh the column references already stored in appliedFilters.
    // This fixes the issue where filters loaded on refresh show raw IDs instead of names,
    // because applyFiltersFromView() runs before Angular propagates the enriched columns back.
    if (changes['availableColumns'] && !changes['availableColumns'].firstChange) {
      this.relinkAppliedFilterColumns();
      this.resolveLateRules();
    }
  }

  private relinkAppliedFilterColumns(): void {
    if (this.appliedFilters.length === 0) return;
    this.appliedFilters = this.appliedFilters.map(f => {
      const updatedCol = this.availableColumns.find(c => c.field === f.column.field);
      return updatedCol ? { ...f, column: updatedCol } : f;
    });
  }

  /**
   * Fetches the options of the module's dropdown custom fields, so their filters offer a picker
   * rather than free text. Every host gets them through `availableColumns`; the filter payload is
   * unchanged (a picked option is the same value a typed one would be).
   */
  private loadCustomFieldOptions(): void {
    const entityType = SavedViewsComponent.CUSTOM_FIELD_ENTITY_TYPES[this.moduleType];
    const prefs = this.userPreferencesService.getUserPreferencesData();
    const companyId = prefs?.selectedCompanyId ?? 0;
    const projectId = prefs?.selectedProjectId ?? 0;
    if (!entityType || !companyId || !projectId) return;

    this.customFieldOptionsSub?.unsubscribe();
    this.customFieldOptionsSub = this.customFieldsService.getDropdownOptions(entityType, companyId, projectId).subscribe({
      next: (options) => {
        this.customFieldOptions = options;
        this.availableColumns = withCustomFieldOptions(this.availableColumns, options);
        this.relinkAppliedFilterColumns();
      },
      // Without options the custom dropdown filters stay free-text, as they were before.
      error: () => { this.customFieldOptions = new Map(); },
    });
    this.subs.add(this.customFieldOptionsSub);
  }

  /** Give the held-back rules their chips once the host supplies their columns. */
  private resolveLateRules(): void {
    if (this.unresolvedRules.length === 0) return;
    const late = this.viewsFilterService.filtersFromRules(this.unresolvedRules, this.availableColumns);
    if (late.length === 0) return;
    this.unresolvedRules = this.rulesWithoutColumn(this.unresolvedRules);
    this.appliedFilters = [...this.appliedFilters, ...late];
    // Part of the view all along, so it must not read as an unsaved edit.
    if (!this.isDirty) {
      this._snapshotOriginalFilters();
    }
  }

  /** Rules no column in `availableColumns` can show (milestone sentinels are not chips at all). */
  private rulesWithoutColumn(rules: ViewFilterRule[]): ViewFilterRule[] {
    return rules.filter(rule =>
      rule.type !== ColumnType.MILESTONE && !this.availableColumns.some(col => col.field === rule.field)
    );
  }

  ngOnDestroy(): void {
    if (this.filterValueDebounceTimer) {
      clearTimeout(this.filterValueDebounceTimer);
    }
    this.resizeObserver?.disconnect();
    this.subs.unsubscribe();
  }

  // ========== UNSAVED CHANGES PROTECTION ==========

  /**
   * Browser refresh / tab close protection.
   * Shows the native browser "Leave site?" prompt when there are unsaved filter changes.
   */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.isDirty || this.isOrderSaving) {
      event.preventDefault();
      // Chrome requires returnValue to be set
      event.returnValue = '';
    }
  }

  /**
   * Returns true if there are unsaved filter changes or a tab reorder is in flight.
   * Used by parent components to implement CanDeactivate route guard.
   */
  hasUnsavedChanges(): boolean {
    return this.isDirty || this.isOrderSaving;
  }

  /**
   * Shows a confirmation dialog for unsaved filter changes.
   * Returns an Observable that emits:
   *  - 'discard' when the user chose to discard changes and proceed
   *  - 'save' when the user chose to save changes and proceed
   *  - null/undefined when the user cancelled (stay on page)
   */
  showUnsavedChangesDialog(): Observable<string | null> {
    const dialogData: CommonConfirmationDialog = {
      confirmMsg: 'Unsaved Filter Changes',
      confirmMsgDescription:
        'You have unsaved filter changes applied to this view that haven\'t been saved. ' +
        'If you leave now, your changes will be lost.',
      icon: 'warning_amber',
      iconClass: '!text-amber-600',
      iconBgColor: '!bg-amber-100',
      compact: true,
      buttons: [
        {
          name: 'Discard',
          class: '!text-base',
          color: 'accent',
          value: 'discard',
        },
        {
          name: 'Save',
          class: '!text-base',
          value: 'save',
        },
      ],
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '540px',
      width: '90%',
      panelClass: 'cmn-dialog',
      data: dialogData,
    });

    return dialogRef.afterClosed();
  }

  // ========== VIEW PILLS HORIZONTAL SCROLL ==========

  /** Called from template (scroll) event */
  onViewsScroll(): void {
    this.checkViewsScrollState();
  }

  scrollViewsLeft(): void {
    const el = this.viewsScrollContainer?.nativeElement;
    if (el) {
      el.scrollBy({ left: -this.scrollAmount, behavior: 'smooth' });
    }
  }

  scrollViewsRight(): void {
    const el = this.viewsScrollContainer?.nativeElement;
    if (el) {
      el.scrollBy({ left: this.scrollAmount, behavior: 'smooth' });
    }
  }

  /** Recalculate whether left/right arrows should be visible */
  private checkViewsScrollState(): void {
    const el = this.viewsScrollContainer?.nativeElement;
    if (!el) {
      this.canScrollLeft = false;
      this.canScrollRight = false;
      return;
    }
    this.canScrollLeft = el.scrollLeft > 0;
    // 1px tolerance for sub-pixel rounding
    this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  }

  /** Scroll the active view pill into the visible area of the scroll container */
  scrollActiveViewIntoView(): void {
    setTimeout(() => {
      const container = this.viewsScrollContainer?.nativeElement;
      if (!container || !this.activeView) return;

      // Find the active pill element inside the container
      const activePill = container.querySelector('.view-pill.active') as HTMLElement;
      if (!activePill) return;

      // Get the pill's wrapper (for user-defined pills it's the cdkDrag div)
      const target = activePill.closest('.view-pill-wrapper') as HTMLElement || activePill;

      const containerRect = container.getBoundingClientRect();
      const pillRect = target.getBoundingClientRect();

      // Check if pill is fully visible
      const isFullyVisible =
        pillRect.left >= containerRect.left && pillRect.right <= containerRect.right;

      if (!isFullyVisible) {
        // Scroll so the pill is centered in the container
        const pillCenter = target.offsetLeft + target.offsetWidth / 2;
        const scrollTo = pillCenter - container.clientWidth / 2;
        container.scrollTo({ left: scrollTo, behavior: 'smooth' });
      }

      // Update arrow states after scroll
      setTimeout(() => this.checkViewsScrollState(), 350);
    });
  }

  // ========== USER DROPDOWN MANAGEMENT ==========

  activeUserFilterIndex = -1;

  /**
   * A popup option list keeps only its visible rows in the DOM, off a memoised search result.
   *
   * A column whose distinct values run into the thousands — a work order reference, a building —
   * otherwise put every one of them in the DOM and re-filtered the whole array on every change
   * detection pass, which is what made opening one of those dropdowns hang the page. Same
   * cdk-virtual-scroll contract as the job code dialog: a fixed row height and a definite viewport
   * height, here capped at MAX_VISIBLE_OPTIONS rows so a two-option column still gets a short panel.
   */
  private static readonly OPTION_ROW_HEIGHT = 32;
  private static readonly MAX_VISIBLE_OPTIONS = 6;
  /** Shared stand-in for a column that carries no options, so the memo below keeps its identity. */
  private static readonly NO_OPTIONS: never[] = [];

  readonly optionRowHeight = SavedViewsComponent.OPTION_ROW_HEIGHT;

  /** The open popup's option viewport — at most one, since only one popup is open at a time. */
  @ViewChildren(CdkVirtualScrollViewport) private optionViewports?: QueryList<CdkVirtualScrollViewport>;

  private _userFilterSearchTerm = '';
  private optionCacheSource: unknown[] | null = null;
  private optionCacheTerm = '';
  private optionCache: unknown[] = [];

  get userFilterSearchTerm(): string {
    return this._userFilterSearchTerm;
  }

  /**
   * Every write — a keystroke, and the resets on open and close — changes the result count, and
   * with it the viewport's height. cdk caches that height and only re-measures when asked, so a
   * list that grows back after a search would otherwise render too few rows to fill itself.
   */
  set userFilterSearchTerm(term: string) {
    this._userFilterSearchTerm = term;
    setTimeout(() => this.optionViewports?.forEach((viewport) => viewport.checkViewportSize()));
  }

  /** Marks every popup this row owns, so a click inside one is not read as a click outside. */
  private static readonly POPUP_SELECTOR = '.sv-popup';

  @HostListener('document:click', ['$event'])
  onDocumentClick(event?: MouseEvent): void {
    // Trigger buttons stopPropagation themselves, so a click reaching document is outside them —
    // except for clicks inside an open popup, which are recognised by ancestry. The popups used to
    // swallow every click with their own handler, which made non-interactive containers
    // "interactive" and cost their contents keyboard support.
    const target = event?.target as HTMLElement | null;
    if (target?.closest?.(SavedViewsComponent.POPUP_SELECTOR)) {
      return;
    }

    if (this.activeUserFilterIndex !== -1) {
      this.closeUserFilterDropdown();
    }
    if (this.showFilterMenu) {
      this.closeFilterMenu();
    }
    if (this.viewDropdownOpen) {
      this.closeViewDropdown();
    }
  }

  toggleUserFilterDropdown(index: number, event: Event): void {
    event.stopPropagation();
    if (this.activeUserFilterIndex === index) {
      this.closeUserFilterDropdown();
    } else {
      this.activeUserFilterIndex = index;
      this.userFilterSearchTerm = '';
      this.userFilterDropdownPosition = this.calculateDropdownPosition(event.currentTarget as HTMLElement, 300, 220);
    }
  }

  closeUserFilterDropdown(): void {
    this.activeUserFilterIndex = -1;
    this.userFilterSearchTerm = '';
  }

  /**
   * The search-filtered options of one list.
   *
   * The result is cached against the source array's identity and the search term. The template
   * asks for the same list several times per pass — the row source, the height, the empty-state
   * check — and does so on every change detection, which is the cost a long list cannot carry.
   * Returning a stable array also keeps cdkVirtualFor from re-rendering every row each pass. One
   * slot is enough: only one popup is open at a time, so only one list is ever being asked for.
   */
  private visibleOptions<T>(source: T[], text: (option: T) => string): T[] {
    if (source !== this.optionCacheSource || this.optionCacheTerm !== this._userFilterSearchTerm) {
      const term = this._userFilterSearchTerm.trim().toLowerCase();
      this.optionCacheSource = source;
      this.optionCacheTerm = this._userFilterSearchTerm;
      this.optionCache = term
        ? source.filter((option) => text(option).toLowerCase().includes(term))
        : source;
    }
    return this.optionCache as T[];
  }

  /** Height of an option list: its rows, capped so a short list does not leave an empty panel. */
  optionListHeight(count: number): number {
    return Math.min(count, SavedViewsComponent.MAX_VISIBLE_OPTIONS) * SavedViewsComponent.OPTION_ROW_HEIGHT;
  }

  trackByOptionId(_index: number, option: { id: string | number }): string | number {
    return option.id;
  }

  trackByOptionValue(_index: number, option: string): string {
    return option;
  }

  getFilteredUsers(): { id: number | string; name: string }[] {
    return this.visibleOptions(this.userOptions, (user) => user.name);
  }

  isUserSelected(filter: FilterItem, userId: string | number): boolean {
    return filter.multiValues?.includes(String(userId)) || false;
  }

  toggleUserMultiSelection(
    index: number,
    userId: string | number,
    event?: Event
  ): void {
    if (event) {
      event.stopPropagation();
      // Space on a focused option would otherwise scroll the popup instead of picking the option.
      if (event.type === 'keydown') {
        event.preventDefault();
      }
    }
    const idStr = String(userId);
    if (!this.appliedFilters[index]) return;

    if (!this.appliedFilters[index].multiValues) {
      this.appliedFilters[index].multiValues = [];
    }

    const multiValues = this.appliedFilters[index].multiValues!;
    const idx = multiValues.indexOf(idStr);

    if (idx > -1) {
      multiValues.splice(idx, 1);
    } else {
      multiValues.push(idStr);
    }

    this.notifyFiltersChanged();
  }

  selectUserFilter(
    index: number,
    userId: string | number,
    event?: Event
  ): void {
    if (event) {
      event.stopPropagation();
      if (event.type === 'keydown') {
        event.preventDefault();
      }
    }
    if (this.appliedFilters[index]) {
      this.appliedFilters[index].value = userId;
      this.closeUserFilterDropdown();
      this.notifyFiltersChanged();
    }
  }

  // ========== DATA LOADING ==========

  /**
   * Load user context from preferences
   */
  private loadUserContext(): void {
    const prefs = this.userPreferencesService.getUserPreferencesData();
    this.currentUserId = prefs?.userId || 0;
  }

  /**
   * Fetch user list via cached service (single API call shared across components)
   */
  private loadUserList(): void {
    this.subs.add(
      this.userListCacheService.getUserOptions$().subscribe({
        next: (options) => {
          // Prepend the special "Unassigned" pseudo-user (id=0) so it appears at the top
          // of every user filter dropdown. Selecting it filters for assignedToUserId = 0.
          const unassignedOption = { id: 0, name: '-' };
          this.userOptions = [unassignedOption, ...options];
          this.userMap = new Map(this.userOptions.map(u => [Number(u.id), u.name]));
        },
        error: (err) => console.error('Failed to load user list:', err),
      })
    );
  }

  /**
   * Check if a column is a user field (createdBy, modifiedBy, etc.)
   */
  isUserField(column: GridColumn): boolean {
    return this.viewsFilterService.isUserField(column);
  }

  /**
   * Get user name by ID from loaded userOptions
   */
  getUserNameById(id: string | number): string {
    const numId = Number(id);
    if (numId === 0) return '-';
    return this.userMap.get(numId) ?? String(id);
  }

  private loadData(): void {
    this.isLoading = true;
    this.hasError = false;

    const prefs = this.userPreferencesService.getUserPreferencesData();
    const companyId = prefs?.selectedCompanyId ?? 0;
    const projectId = prefs?.selectedProjectId ?? 0;

    this.subs.add(
      this.reportingViewsService.listSessions(companyId, projectId, undefined, this.moduleSegment).pipe(
        switchMap(sessions => {
          const { activeView } = this.processSessionViews(sessions);

          return this.reportingViewsService
            .getColumnOptions(companyId, projectId, activeView?.id, this.moduleSegment)
            .pipe(map(columns => ({ columns, activeView })));
        }),
        finalize(() => {
          this.isLoading = false;
          // After Angular renders the pills, scroll active view into sight and update arrows
          this.scrollActiveViewIntoView();
        })
      ).subscribe({
        next: ({ columns, activeView }) => {
          this.processColumns(columns);
          if (columns.custom.length > 0) this.loadCustomFieldOptions();

          // Fallback: if no active view found, select first view as safety net
          const viewToApply = activeView || (this.visibleViews.length > 0 ? this.visibleViews[0] : null);
          this.applyActiveView(viewToApply);
        },
        error: (err) => {
          this.hasError = true;
          console.error('Failed to load data:', err);
        },
      })
    );
  }

  private processSessionViews(sessions: { views: WorkPackageView[]; activeViewId: number | null }): {
    visibleViews: WorkPackageView[];
    activeView: WorkPackageView | null;
  } {
    // Filter out views with empty names
    const sessionViews = (sessions.views ?? []).filter(
      view => view?.name?.trim().length > 0
    );

    // Always prepend the frontend-only default view
    this.visibleViews = [this.defaultView, ...sessionViews];

    // In compact (dashboard-widget) mode, prefer the layout-scoped restoreViewId so
    // each widget independently remembers its own selection instead of sharing the
    // module-level session (which is user/project-wide, not layout-specific).
    const effectiveActiveId = (this.compactMode && this.restoreViewId && this.restoreViewId > 0)
      ? this.restoreViewId
      : sessions.activeViewId;

    // Determine active view from effective ID, or fall back to default view
    let activeView = effectiveActiveId
      ? sessionViews.find(v => v.id === effectiveActiveId) ?? this.defaultView
      : this.defaultView;

    // A system view picked last time on this list page wins on the first load: the sessions API
    // can't record one (it needs a real viewId), so it still marks the named view used before.
    // Later reloads (after creating or opening a view) follow the server's active view.
    if (this.restoreSystemViewOnLoad) {
      this.restoreSystemViewOnLoad = false;
      const systemId = this.compactMode ? null : this.localStorageService.getItem<number>(this.getActiveSystemViewStorageKey());
      activeView = [this.defaultView, this.archiveView, this.draftView].find(v => v.id === systemId) ?? activeView;
    }

    return { visibleViews: this.visibleViews, activeView };
  }

  private applyActiveView(activeView: WorkPackageView | null): void {
    // Fall back to default view if nothing is active
    let viewToApply = activeView ?? this.defaultView;

    // Emit viewApplying to signal parent component to show loading state
    // before the saved-views skeleton disappears
    this.viewApplying.emit();

    // Suppress filter emit while applying view filters
    this._suppressFilterEmit = true;

    // For default view, try to restore filters saved in session storage
    if (this.isDefaultView(viewToApply)) {
      const savedFilters = this.getDefaultViewFiltersFromSession();
      if (savedFilters) {
        viewToApply = { ...viewToApply, filters: savedFilters };
      }
    }

    this.applyFiltersFromView(viewToApply);
    this._suppressFilterEmit = false;

    // Snapshot the original filter state so we can detect dirty state and revert
    this._snapshotOriginalFilters();

    this.activeView = viewToApply;
    this.isDirty = false;
    this.viewApplied.emit(viewToApply);
  }

  /**
   * Process and store column data
   * @param columns - Column options containing standard and custom columns
   */
  private processColumns(columns: { standard: GridColumn[]; custom: GridColumn[]; milestone?: GridColumn[] }): void {
    const { standard, custom, milestone = [] } = columns;

    this.standardColumns = standard;
    this.customColumns = custom;

    const allColumns = withCustomFieldOptions([...standard, ...custom, ...milestone], this.customFieldOptions);
    if (allColumns.length > 0) {
      this.availableColumns = allColumns;
      this.columnsLoaded.emit(allColumns);
    }
    // Prefetch the Tags picker's options in the background, alongside the grid's first fetch,
    // so the picker is ready when opened rather than loading then.
    if (allColumns.some(col => col.field?.toLowerCase() === 'tags')) {
      this.loadTagOptions();
    }
  }

  /**
   * Refresh columns and sessions from API
   */
  refreshData(): void {
    this.loadData();
  }

  /**
   * Load all views from API (called lazily when needed)
   */
  private loadAllViews(): void {
    this.subs.add(
      this.reportingViewsService.listViews(undefined, undefined, undefined, undefined, this.moduleSegment).subscribe({
        next: (views) => {
          this.views = views;
          this.categorizeViews();
        },
        error: (err) => {
          console.error('Failed to load views:', err);
        },
      }),
    );
  }

  // ========== VIEW MANAGEMENT ==========

  /**
   * Categorize views into different groups
   */
  private categorizeViews(): void {
    this.categorizedViews = {
      colobboProvided: this.views.filter(v => v.category === ViewCategory.ColobboProvided),
      adminPromoted: this.views.filter(v => v.category === ViewCategory.AdminPromoted),
      createdByMe: this.views.filter(v => v.ownerId === this.currentUserId),
      createdByOthers: this.views.filter(v => v.ownerId !== this.currentUserId),
    };
    this._userCreatedViewsCount = this.views.filter(
      v => v.category === ViewCategory.UserCreated && v.ownerId === this.currentUserId
    ).length;
  }

  /**
   * Check if a view is currently active
   */
  isActiveView(view: WorkPackageView): boolean {
    return this.activeView?.id === view.id;
  }

  /**
   * Check if a view is the frontend-only default view
   */
  isDefaultView(view: WorkPackageView): boolean {
    return view.id === this.DEFAULT_VIEW_ID;
  }

  /**
   * Check if a view is any local-only system view (default, archive, draft).
   * System views are never stored in the DB and cannot be removed.
   */
  isSystemView(view: WorkPackageView): boolean {
    return view.id <= 0;
  }

  /**
   * Returns true when the currently active view is the frontend-only default view.
   * Used to disable filter controls in the template.
   */
  get isActiveDefaultView(): boolean {
    return this.activeView?.id === this.DEFAULT_VIEW_ID;
  }

  // ========== DEFAULT VIEW LOCAL STORAGE PERSISTENCE ==========

  /**
   * Build a localStorage key scoped to module type, company, and project.
   *
   * In compact (dashboard-widget) mode the key is additionally namespaced with a
   * `dashboard_` segment so default-view filters set inside a widget are stored
   * separately and never leak into the actual module grid (or vice versa). The
   * list page (compactMode = false) keeps its original key unchanged.
   */
  private getDefaultViewStorageKey(): string {
    return `defaultViewFilters_${this.compactMode ? 'dashboard_' : ''}${this.storageScope()}`;
  }

  /** List page only: the system view (Default/Archive/Draft) the user picked last, if that was one. */
  private getActiveSystemViewStorageKey(): string {
    return `activeSystemView_${this.storageScope()}`;
  }

  private storageScope(): string {
    const prefs = this.userPreferencesService.getUserPreferencesData();
    return `${this.moduleType}_${prefs?.userId ?? 0}_${prefs?.selectedCompanyId ?? 0}_${prefs?.selectedProjectId ?? 0}`;
  }

  /** First session load still to restore a stored system view (see processSessionViews). */
  private restoreSystemViewOnLoad = true;

  /**
   * Persist the current default-view filter payload into localStorage.
   * Removes the entry when there are no filters to avoid stale data.
   */
  private saveDefaultViewFiltersToSession(): void {
    const payload = this.getFiltersAsPayload();
    if (payload?.rules?.length > 0) {
      this.localStorageService.setItem(this.getDefaultViewStorageKey(), payload);
    } else {
      this.localStorageService.removeItem(this.getDefaultViewStorageKey());
    }
  }

  /**
   * Retrieve saved default-view filters from localStorage.
   * Falls back to the host's `defaultViewFilters` (null unless set) when nothing valid is stored.
   */
  private getDefaultViewFiltersFromSession(): ViewFiltersPayload | null {
    const payload = this.localStorageService.getItem<ViewFiltersPayload>(this.getDefaultViewStorageKey());
    if (payload?.rules?.length > 0) return payload;
    return this.defaultViewFilters;
  }

  /**
   * Apply a view — sets it as active both locally and via API,
   * populates the filter builder from the view's saved filters,
   * fetches view-specific columns, and emits filtersChanged so the parent receives them.
   */
  applyView(view: WorkPackageView): void {
    // Guard: if the user has unsaved filter changes on the current view,
    // show a confirmation dialog before switching to a different view.
    if (this.isDirty && this.activeView && this.activeView.id !== view.id) {
      this.subs.add(
        this.showUnsavedChangesDialog().subscribe(result => {
          if (result === 'save') {
            // Save first — wait for API to complete, then switch view
            this.subs.add(
              this.saveCurrentViewAsync().subscribe(() => {
                this._applyViewInternal(view);
              })
            );
          } else if (result === 'discard') {
            // Discard changes and switch immediately
            this._applyViewInternal(view);
          }
          // null/undefined = 'Stay on this view' (dialog closed) — do nothing
        })
      );
      return;
    }

    this._applyViewInternal(view);
  }

  /**
   * Internal method that performs the actual view switch.
   * Extracted from applyView() to allow bypassing the unsaved-changes guard.
   */
  private _applyViewInternal(view: WorkPackageView): void {
    // Dashboard widgets only (list pages keep their existing behaviour): a value typed just
    // before the switch must not land on the new view once its debounce fires.
    if (this.compactMode && this.filterValueDebounceTimer) {
      clearTimeout(this.filterValueDebounceTimer);
      this.filterValueDebounceTimer = null;
    }
    this.activeView = view;
    this.isDirty = false;
    this.scrollActiveViewIntoView();
    this.viewApplying.emit();

    const isDefault = this.isDefaultView(view);
    const isSystem = this.isSystemView(view);
    const savedFilters = isDefault ? this.getDefaultViewFiltersFromSession() : null;
    const viewToApply = savedFilters ? { ...view, filters: savedFilters } : view;

    this._suppressFilterEmit = true;
    if (isSystem && !isDefault) {
      // Archive/Draft: clear the filter builder — their rules are handled entirely in buildViewPayload()
      this.appliedFilters = [];
      this.filterLogic = 'AND';
    } else {
      this.applyFiltersFromView(viewToApply);
    }
    this._suppressFilterEmit = false;

    // Snapshot the original filter state for this view so we can detect dirty state and revert
    this._snapshotOriginalFilters();

    // Widgets keep their selection in the dashboard layout instead.
    if (!this.compactMode) {
      if (isSystem) this.localStorageService.setItem(this.getActiveSystemViewStorageKey(), view.id);
      else this.localStorageService.removeItem(this.getActiveSystemViewStorageKey());
    }

    if (isSystem) {
      this.viewApplied.emit(viewToApply);
      return;
    }

    const { selectedCompanyId: companyId = 0, selectedProjectId: projectId = 0, userId = 0 } =
      this.userPreferencesService.getUserPreferencesData() ?? {};

    this.reportingViewsService.setActiveSession(
      { companyId, projectId, userId, viewId: view.id },
      this.moduleSegment
    ).subscribe();

    this.subs.add(
      this.reportingViewsService.getColumnOptions(companyId, projectId, view.id, this.moduleSegment).subscribe({
        next: (columns) => {
          this.processColumns(columns);
          this.viewApplied.emit(view);
        },
        error: (err) => {
          console.error(`Failed to load columns for view ${view.id}:`, err);
          this.viewApplied.emit(view);
        },
      })
    );
  }

  /** Capture the current filter state as the "clean" baseline (stores serializable payload, not FilterItem[]) */
  private _snapshotOriginalFilters(): void {
    this._originalViewFilters = {
      filters: JSON.parse(JSON.stringify(this.getFiltersAsPayload())),
      logic: this.filterLogic,
    };
    this.isDirty = false;
  }

  /** Recompute dirty flag after any filter change */
  private _markDirtyIfChanged(): void {
    if (!this._originalViewFilters || this.isSystemView(this.activeView!)) {
      this.isDirty = false;
      return;
    }
    const currentJson = JSON.stringify(this.getFiltersAsPayload());
    const originalJson = JSON.stringify(this._originalViewFilters.filters);
    this.isDirty = currentJson !== originalJson || this.filterLogic !== this._originalViewFilters.logic;
  }

  /**
   * Save the current filter state to the DB for the active named view.
   * Called only when the user explicitly presses the Save button.
   */
  saveCurrentView(): void {
    if (!this.activeView || this.isSystemView(this.activeView) || this.isSavingView) return;

    const viewPayload = this.buildViewPayload();
    this.isSavingView = true;

    this.subs.add(
      this.reportingViewsService.saveView(viewPayload, this.moduleSegment).pipe(
        finalize(() => { this.isSavingView = false; })
      ).subscribe({
        next: (response) => {
          if (response?.success && this.activeView) {
            this.activeView.filters = this.getFiltersAsPayload();
            this._snapshotOriginalFilters();
          }
        },
        error: (err) => {
          console.error('Error saving view:', err);
        },
      })
    );
  }

  /**
   * Save the current view and return an Observable that completes only after the
   * API call finishes (success or error). Used by the route guard so navigation
   * is deferred until the save is fully committed.
   */
  saveCurrentViewAsync(): Observable<boolean> {
    if (!this.activeView || this.isSystemView(this.activeView) || this.isSavingView) {
      return of(true);
    }

    const viewPayload = this.buildViewPayload();
    this.isSavingView = true;

    return this.reportingViewsService.saveView(viewPayload, this.moduleSegment).pipe(
      map((response) => {
        if (response?.success && this.activeView) {
          this.activeView.filters = this.getFiltersAsPayload();
          this._snapshotOriginalFilters();
        }
        return true;
      }),
      finalize(() => { this.isSavingView = false; }),
      // On error, still allow navigation — don't block the user
      // (error is logged but navigation proceeds)
      catchError((err) => {
        console.error('Error saving view before navigation:', err);
        return of(true);
      })
    );
  }

  /**
   * Revert the filter builder to the last saved state.
   * Does NOT call the API — restores the snapshotted ViewFiltersPayload via applyFiltersFromView
   * so that GridColumn references are properly re-linked from availableColumns.
   */
  revertView(): void {
    if (!this._originalViewFilters) return;

    this._suppressFilterEmit = true;
    // Use a synthetic view so applyFiltersFromView can re-link GridColumn objects properly
    const syntheticView = {
      ...this.activeView,
      filters: this._originalViewFilters.filters as ViewFiltersPayload,
    } as WorkPackageView;
    this.applyFiltersFromView(syntheticView);
    this.filterLogic = this._originalViewFilters.logic;
    this._suppressFilterEmit = false;

    this.isDirty = false;
    this.emitFiltersChanged();
  }

  /**
   * Open the Edit Columns dialog for the active named view.
   * Saves directly to DB when the user clicks "Save Columns".
   */
  openEditColumnsDialog(): void {
    if (!this.activeView || this.isSystemView(this.activeView)) return;

    const prefs = this.userPreferencesService.getUserPreferencesData();

    const dialogRef = this.dialog.open(CreateViewDialogComponent, {
      width: '100%',
      maxWidth: '700px',
      panelClass: 'cmn-dialog',
      injector: this.injector,
      data: {
        view: this.activeView,
        companyId: prefs?.selectedCompanyId ?? 0,
        projectId: prefs?.selectedProjectId ?? 0,
        userId: this.currentUserId,
        userName: prefs?.userName ?? 'User',
        moduleType: this.moduleType,
        dashboardContext: this.compactMode,
        availableColumns: this.availableColumns,
        userOptions: this.userOptions,
        milestoneOptions: this.isWorkPackageModule() ? this.milestoneOptions : [],
        columnsOnlyMode: true,
      },
    });

    this.subs.add(
      dialogRef.afterClosed().pipe(
        filter(result => result?.action === 'created' || result?.action === 'updated'),
        switchMap(() => this.reportingViewsService.listViews(undefined, undefined, undefined, undefined, this.moduleSegment))
      ).subscribe({
        next: (views) => {
          this.views = views;
          this.categorizeViews();
          this.updateViewsListDialog();
          this.refreshData();
        },
        error: (err) => console.error('Failed to reload views after column edit:', err),
      })
    );
  }

  /**
   * Open Clone View dialog — pre-fills the new view with the active view's current
   * (possibly unsaved) filters and columns so the user can save a copy.
   */
  openCloneViewDialog(): void {
    if (!this.activeView || this.isSystemView(this.activeView)) return;

    const prefs = this.userPreferencesService.getUserPreferencesData();

    // Build a synthetic view that includes the current (possibly dirty) filter state
    const viewWithCurrentFilters: WorkPackageView = {
      ...this.activeView,
      id: 0, // id=0 tells CreateViewDialog this is a new view
      name: `Copy of ${this.activeView.name}`,
      filters: this.getFiltersAsPayload(),
    };

    const dialogRef = this.dialog.open(CreateViewDialogComponent, {
      width: '100%',
      maxWidth: '950px',
      panelClass: 'cmn-dialog',
      injector: this.injector,
      data: {
        view: viewWithCurrentFilters,
        companyId: prefs?.selectedCompanyId ?? 0,
        projectId: prefs?.selectedProjectId ?? 0,
        userId: this.currentUserId,
        userName: prefs?.userName ?? 'User',
        moduleType: this.moduleType,
        dashboardContext: this.compactMode,
        availableColumns: this.availableColumns,
        userOptions: this.userOptions,
        milestoneOptions: this.isWorkPackageModule() ? this.milestoneOptions : [],
        cloneMode: true,
      },
    });

    this.subs.add(
      dialogRef.afterClosed().pipe(
        filter(result => result?.action === 'created' || result?.action === 'updated'),
        switchMap((result) => {
          if (result?.action === 'created' && result?.viewId) {
            const tabOrder = this.visibleViews.length + 1;
            const ctx = {
              companyId: prefs?.selectedCompanyId ?? 0,
              projectId: prefs?.selectedProjectId ?? 0,
              userId: this.currentUserId,
            };
            return this.reportingViewsService.upsertSession({
              ...ctx,
              viewId: result.viewId,
              isActive: true,
              tabOrder,
            }, this.moduleSegment).pipe(
              switchMap(() => this.reportingViewsService.setActiveSession({ ...ctx, viewId: result.viewId }, this.moduleSegment)),
              switchMap(() => this.reportingViewsService.listViews(undefined, undefined, undefined, undefined, this.moduleSegment))
            );
          }
          return this.reportingViewsService.listViews(undefined, undefined, undefined, undefined, this.moduleSegment);
        })
      ).subscribe({
        next: (views) => {
          this.views = views;
          this.categorizeViews();
          this.updateViewsListDialog();
          this.refreshData();
        },
        error: (err) => console.error('Failed to load views after clone:', err),
      })
    );
  }

  /**
   * Fields that are always applied as default filters for work-order module.
   * They are prepended server-side and should not appear in the UI filter builder.
   */
  private readonly WO_DEFAULT_FILTER_FIELDS = ['parentworkorderid', 'recordstate'];

  /**
   * Populate the inline filter builder from a view's saved filters.
   * Maps ViewFilterRule[] → FilterItem[] and sets the filter logic.
   */
  private applyFiltersFromView(view: WorkPackageView): void {
    const viewFilters = view.filters;

    // For work-order module, strip the default rules so they don't appear in the builder
    const rules = this.moduleType === 'work-order'
      ? (viewFilters?.rules || []).filter(
          r => !this.WO_DEFAULT_FILTER_FIELDS.includes(r.field?.toLowerCase() || '')
        )
      : (viewFilters?.rules || []);

    this.unresolvedRules = this.rulesWithoutColumn(rules);

    if (rules.length === 0) {
      // No saved filters — clear the builder
      this.appliedFilters = [];
      this.filterLogic = 'AND';
      this.emitFiltersChanged();
      return;
    }

    // Set filter logic from the view
    this.filterLogic = (viewFilters?.logic as 'AND' | 'OR') || 'AND';

    // Restore shared milestone ID. Try all three formats — options may or may not be loaded yet.
    this.sharedMilestoneId = this.resolveSharedMilestoneId(rules);

    // Convert rules to FilterItems using service (milestoneName rule is skipped inside)
    this.appliedFilters = this.viewsFilterService.filtersFromRules(
      rules,
      this.availableColumns,
    );

    // Lazily load tag options if any filter targets the tags field
    if (rules.some(r => r.field?.toLowerCase() === 'tags')) {
      this.loadTagOptions();
    }

    // Emit so parent receives the updated filters
    this.emitFiltersChanged();
  }

  /**
   * Build the current filter builder state as ViewFilterRule[] for saving.
   */
  getFiltersAsPayload(): ViewFiltersPayload {
    return this.viewsFilterService.buildFiltersPayload(
      this.appliedFilters,
      this.filterLogic,
      this.sharedMilestoneId || undefined,
    ) as ViewFiltersPayload;
  }

  /**
   * Clear active view
   */
  clearView(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.activeView = null;
    this.appliedFilters = [];
    this.filterLogic = 'AND';
    this.emitFiltersChanged();
    this.viewCleared.emit();
  }

  /**
   * Check if user can edit a view
   */
  canEdit(view: WorkPackageView): boolean {
    if (view.category === ViewCategory.ColobboProvided || view.isDefault) {
      return false;
    }
    return view.ownerId === this.currentUserId;
  }

  /**
   * Edit/Rename a view
   */
  editView(view: WorkPackageView, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    // In compact (dashboard widget) mode any non-system view can be opened — the
    // widget never writes back to the module view tables, so ownership is irrelevant.
    if (!this.compactMode && !this.canEdit(view)) {
      return;
    }
    if (this.isSystemView(view)) {
      return;
    }

    const prefs = this.userPreferencesService.getUserPreferencesData();

    // In a bound dashboard widget, seed the dialog from the dashboard view's
    // saved columns/filters/sort (what the widget actually shows) — not the
    // module view's — while keeping the source view's name/id for display.
    const dialogView: WorkPackageView =
      this.compactMode && this.boundDashboardViewId && this.dashboardViewDefinition
        ? {
            ...view,
            columns: this.dashboardViewDefinition.columns,
            filters: this.dashboardViewDefinition.filters,
            sort: this.dashboardViewDefinition.sort,
          }
        : view;

    const dialogRef = this.dialog.open(CreateViewDialogComponent, {
      width: '100%',
      maxWidth: '950px',
      panelClass: 'cmn-dialog',
      injector: this.injector,
      data: {
        view: dialogView,
        companyId: prefs?.selectedCompanyId ?? 0,
        projectId: prefs?.selectedProjectId ?? 0,
        userId: this.currentUserId,
        userName: prefs?.userName ?? 'User',
        moduleType: this.moduleType,
        dashboardContext: this.compactMode,
        dashboardViewId: this.compactMode ? (this.boundDashboardViewId ?? undefined) : undefined,
        dashboardViewName: this.compactMode ? this.dashboardViewName : undefined,
        availableColumns: this.availableColumns,
        userOptions: this.userOptions,
        milestoneOptions: this.isWorkPackageModule() ? this.milestoneOptions : [],
      },
    });

    this.subs.add(
      dialogRef.afterClosed().pipe(
        filter(result => result?.action === 'created' || result?.action === 'updated'),
        tap(result => {
          // In a dashboard widget, bind the widget to the dashboard view that was just saved.
          if (this.compactMode && result?.viewId) this.dashboardViewSaved.emit(result.viewId);
        }),
        switchMap(() => this.reportingViewsService.listViews(undefined, undefined, undefined, undefined, this.moduleSegment))
      ).subscribe({
        next: (views) => {
          this.views = views;
          this.categorizeViews();
          this.updateViewsListDialog();
          this.refreshData();
        },
        error: (err) => {
          console.error('Failed to load views:', err);
        }
      })
    );
  }

  /**
   * Open views list dialog
   */
  openViewsListDialog(): void {
    const isLoadingViews = this.views.length === 0;

    this.viewsListDialogRef = this.dialog.open(ViewsListDialogComponent, {
      width: '100%',
      maxWidth: '650px',
      height: '100vh',
      position: { right: '0', top: '0' },
      panelClass: 'views-list-dialog',
      autoFocus: false,
      data: {
        views: this.views,
        categorizedViews: this.categorizedViews,
        activeView: this.activeView,
        currentUserId: this.currentUserId,
        isLoading: isLoadingViews,
      },
    });

    // Load all views if not already loaded
    if (isLoadingViews) {
      this.subs.add(
        this.reportingViewsService.listViews(undefined, undefined, undefined, undefined, this.moduleSegment).subscribe({
          next: (views) => {
            this.views = views;
            this.categorizeViews();
            // Update the dialog with the loaded data
            if (this.viewsListDialogRef?.componentInstance) {
              this.viewsListDialogRef.componentInstance.updateViewsData(
                this.views,
                this.categorizedViews,
                this.reportingViewsService.lastListViewsFailed
              );
            }
          },
          error: (err) => {
            console.error('Failed to load views:', err);
            // listViews() swallows its own errors and resolves an empty array, so this
            // branch is a defensive fallback in case that ever changes.
            if (this.viewsListDialogRef?.componentInstance) {
              this.viewsListDialogRef.componentInstance.isLoading = false;
              this.viewsListDialogRef.componentInstance.hasError = true;
            }
          },
        }),
      );
    }

    this.viewsListDialogRef.afterClosed().subscribe((result) => {
      this.viewsListDialogRef = null; // Clear the reference
      if (result) {
        if ('action' in result && result.action === 'create') {
          this.openCreateViewDialog();
        } else {
          // User selected a view — open it as a tab and make it active
          this.openViewAsTab(result as WorkPackageView);
        }
      }
    });
  }

  /**
   * Open a view as a tab (upsert session) and make it active
   */
  private openViewAsTab(view: WorkPackageView): void {
    const prefs = this.userPreferencesService.getUserPreferencesData();
    const ctx = {
      companyId: prefs?.selectedCompanyId || 0,
      projectId: prefs?.selectedProjectId || 0,
      userId: prefs?.userId || 0,
    };

    // Check if view is already in visible tabs
    const alreadyOpen = this.visibleViews.some((v) => v.id === view.id);

    if (!alreadyOpen) {
      // Upsert session to add the tab
      const tabOrder = this.visibleViews.length + 1;
      this.subs.add(
        this.reportingViewsService
          .upsertSession({
            ...ctx,
            viewId: view.id,
            isActive: true,
            tabOrder,
          }, this.moduleSegment)
          .subscribe({
            next: () => {
              // Add to visible views locally
              this.visibleViews.push(view);
              this.applyView(view);
              setTimeout(() => this.checkViewsScrollState());
            },
          }),
      );
    } else {
      // Already open — just activate it
      this.applyView(view);
    }
  }

  /**
   * Open create view dialog
   */
  openCreateViewDialog(): void {
    const prefs = this.userPreferencesService.getUserPreferencesData();

    const dialogRef = this.dialog.open(CreateViewDialogComponent, {
      width: '100%',
      maxWidth: '950px',
      panelClass: 'cmn-dialog',
      injector: this.injector,
      data: {
        companyId: prefs?.selectedCompanyId ?? 0,
        projectId: prefs?.selectedProjectId ?? 0,
        userId: this.currentUserId,
        userName: prefs?.userName ?? 'User',
        moduleType: this.moduleType,
        dashboardContext: this.compactMode,
        availableColumns: this.availableColumns,
        userOptions: this.userOptions,
        milestoneOptions: this.isWorkPackageModule() ? this.milestoneOptions : [],
      },
    });

    this.subs.add(
      dialogRef.afterClosed().pipe(
        filter(result => result?.action === 'created' || result?.action === 'updated'),
        switchMap((result) => {
          // For new views, upsert a session first so it appears in the visible tabs,
          // then explicitly mark it active so it is selected by default.
          if (result?.action === 'created' && result?.viewId) {
            const tabOrder = this.visibleViews.length + 1;
            const ctx = {
              companyId: prefs?.selectedCompanyId ?? 0,
              projectId: prefs?.selectedProjectId ?? 0,
              userId: this.currentUserId,
            };
            return this.reportingViewsService.upsertSession({
              ...ctx,
              viewId: result.viewId,
              isActive: true,
              tabOrder,
            }, this.moduleSegment).pipe(
              switchMap(() => this.reportingViewsService.setActiveSession({ ...ctx, viewId: result.viewId }, this.moduleSegment)),
              switchMap(() => this.reportingViewsService.listViews(undefined, undefined, undefined, undefined, this.moduleSegment))
            );
          }
          return this.reportingViewsService.listViews(undefined, undefined, undefined, undefined, this.moduleSegment);
        })
      ).subscribe({
        next: (views) => {
          this.views = views;
          this.categorizeViews();
          this.updateViewsListDialog();
          this.refreshData();
        },
        error: (err) => {
          console.error('Failed to load views:', err);
        }
      })
    );
  }

  /**
   * Update the views list dialog if it's still open
   */
  private updateViewsListDialog(): void {
    this.viewsListDialogRef?.componentInstance?.updateViewsData(
      this.views,
      this.categorizedViews
    );
  }

  /**
   * Navigate to All Views page (passes module as query param)
   */
  navigateToAllViews(): void {
    this.router.navigate([this.allViewsRoute], {
      queryParams: { module: this.moduleSegment },
    });
  }

  /**
   * Get count of views in a category
   */
  getCategoryCount(category: keyof typeof this.categorizedViews): number {
    return this.categorizedViews[category]?.length || 0;
  }

  /**
   * Check if any views exist
   */
  hasAnyViews(): boolean {
    return this.views.length > 0;
  }

  /**
   * Get count of user-created views (for current user)
   */
  getUserCreatedViewsCount(): number {
    return this._userCreatedViewsCount;
  }

  /**
   * Handle view pill drag and drop reordering.
   *
   * CDK drop indices are relative to draggable items only (user-defined views).
   * `visibleViews` includes the non-draggable default view at index 0, so we
   * operate on the user-defined slice and then rebuild visibleViews.
   *
   * We send the full new ordering as a bulk update so every shifted view has
   * its tab_order persisted atomically — a single-view update would leave the
   * other affected views with stale (duplicate) tab_orders and the reorder
   * would not survive a refresh.
   */
  onViewDrop(event: CdkDragDrop<WorkPackageView[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const userViews = this.visibleViews.filter((v) => !this.isDefaultView(v));
    if (
      event.previousIndex < 0 ||
      event.previousIndex >= userViews.length ||
      event.currentIndex < 0 ||
      event.currentIndex >= userViews.length
    ) {
      return;
    }

    // Snapshot before mutating so we can revert if the API fails
    const previousOrder = [...userViews];

    moveItemInArray(userViews, event.previousIndex, event.currentIndex);

    // Rebuild visibleViews preserving the default view at position 0
    this.visibleViews = [this.defaultView, ...userViews];

    const prefs = this.userPreferencesService.getUserPreferencesData();
    const companyId = prefs?.selectedCompanyId || 0;
    const projectId = prefs?.selectedProjectId || 0;
    const userId = prefs?.userId || 0;

    if (!companyId || !projectId || !userId) {
      // Context not ready — revert the optimistic UI update silently
      this.visibleViews = [this.defaultView, ...previousOrder];
      return;
    }

    const orders = userViews.map((view, idx) => ({
      viewId: view.id,
      tabOrder: idx + 1,
    }));

    this.isOrderSaving = true;
    this.subs.add(
      this.reportingViewsService
        .setSessionOrder({ companyId, projectId, userId, orders }, this.moduleSegment)
        .pipe(
          finalize(() => { this.isOrderSaving = false; }),
          catchError((err) => {
            console.error('Failed to save view order:', err);
            // Revert optimistic UI update so the stale order is not shown
            this.visibleViews = [this.defaultView, ...previousOrder];
            return of(null);
          })
        )
        .subscribe(),
    );
  }

  /**
   * Remove a view from visible views (close tab)
   */
  removeFromVisibleViews(view: WorkPackageView, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (this.isSystemView(view)) {
      return;
    }

    const index = this.visibleViews.findIndex((v) => v.id === view.id);
    if (index !== -1) {
      const wasActive = this.isActiveView(view);

      // Remove locally
      this.visibleViews.splice(index, 1);
      setTimeout(() => this.checkViewsScrollState());

      // Close session via API
      const prefs = this.userPreferencesService.getUserPreferencesData();
      this.subs.add(
        this.reportingViewsService
          .closeSession({
            companyId: prefs?.selectedCompanyId || 0,
            projectId: prefs?.selectedProjectId || 0,
            userId: prefs?.userId || 0,
            viewId: view.id,
          }, this.moduleSegment)
          .subscribe(),
      );

      // If the removed view was active, select the next one
      if (wasActive) {
        this.selectNextAvailableView();
      }
    }
  }

  /**
   * Select the next available view after removing the active one
   */
  private selectNextAvailableView(): void {
    if (this.visibleViews.length > 0) {
      this.applyView(this.visibleViews[0]);
    } else {
      this.clearView();
    }
  }

  // ========== FILTER BUILDER METHODS ==========

  /**
   * Toggle filter logic between AND and OR
   */
  toggleFilterLogic(): void {
    this.filterLogic = this.filterLogic === 'AND' ? 'OR' : 'AND';
    this.notifyFiltersChanged();
  }

  /**
   * Set filter logic to a specific value
   */
  setFilterLogic(logic: 'AND' | 'OR'): void {
    if (this.filterLogic !== logic) {
      this.filterLogic = logic;
      this.notifyFiltersChanged();
    }
  }

  /**
   * Toggle filter menu dropdown
   */
  toggleFilterMenu(event?: MouseEvent): void {
    event?.stopPropagation();
    this.showFilterMenu = !this.showFilterMenu;
    if (this.showFilterMenu && event) {
      this.filterMenuPosition = this.calculateDropdownPosition(event.currentTarget as HTMLElement, 400, 200);
    }
    if (!this.showFilterMenu) {
      this.filterSearchTerm = '';
    }
  }

  /**
   * Close filter menu
   */
  closeFilterMenu(): void {
    this.showFilterMenu = false;
    this.filterSearchTerm = '';
  }

  /**
   * Calculate smart dropdown position based on available viewport space.
   * Returns one of: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'
   */
  private calculateDropdownPosition(
    trigger: HTMLElement,
    dropdownHeight: number,
    dropdownWidth: number
  ): string {
    if (!trigger) return 'bottom-left';

    const { top, bottom, left } = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const safeWidth = dropdownWidth > 320 ? dropdownWidth : 320;
    const safeHeight = dropdownHeight > 320 ? dropdownHeight : 320;

    const spaceBelow = viewportHeight - bottom;
    const spaceAbove = top;
    const spaceRight = viewportWidth - left;

    const openAbove = spaceBelow < safeHeight && spaceAbove > spaceBelow;
    const alignRight = spaceRight < safeWidth || left > viewportWidth * 0.6;

    if (openAbove) {
      return alignRight ? 'top-right' : 'top-left';
    }

    return alignRight ? 'bottom-right' : 'bottom-left';
  }

  /**
   * Get columns available for filtering (excludes already applied filters and non-meaningful ID fields)
   */
  getAvailableColumnsForFilter(): GridColumn[] {
    return this.viewsFilterService.getAvailableColumnsForFilter(
      this.availableColumns,
      this.appliedFilters,
    );
  }

  /**
   * Get filtered columns based on search term
   */
  getFilteredColumnsForFilter(): GridColumn[] {
    const availableColumns = this.getAvailableColumnsForFilter();
    if (!this.filterSearchTerm.trim()) {
      return availableColumns;
    }
    const searchLower = this.filterSearchTerm.toLowerCase();
    return availableColumns.filter(col =>
      col.headerText.toLowerCase().includes(searchLower)
    );
  }

  /**
   * Get dynamic operators for a column based on its dataType.
   * For user fields, use dropdown operators instead.
   */
  getOperatorsForColumn(column: GridColumn): FilterOperatorDef[] {
    return this.viewsFilterService.getOperatorsForColumn(
      column,
      this.viewsFilterService.isUserField(column),
    );
  }

  /**
   * Check if the current operator for a filter is a 'between' type
   */
  isBetween(operator: string): boolean {
    return this.viewsFilterService.isBetween(operator);
  }

  /**
   * Check if the current operator for a filter is a multi-value type (in/notin)
   */
  isMulti(operator: string): boolean {
    return this.viewsFilterService.isMulti(operator);
  }

  isNoValue(operator: string): boolean {
    return this.viewsFilterService.isNoValue(operator);
  }

  /**
   * Get input type based on column dataType
   */
  getInputType(column: GridColumn): string {
    return this.viewsFilterService.getInputType(column);
  }

  /**
   * Check if a column is a numeric type
   */
  isNumberField(column: GridColumn): boolean {
    return this.viewsFilterService.isNumberField(column);
  }

  /**
   * Get context-aware placeholder for a filter value input
   */
  getPlaceholder(column: GridColumn, boundary?: 'start' | 'end'): string {
    return this.viewsFilterService.getPlaceholder(column, boundary);
  }

  /**
   * Check if a column is a dropdown type
   */
  isDropdownColumn(column: GridColumn): boolean {
    return this.viewsFilterService.isDropdownColumn(column, this.userOptions);
  }

  /**
   * Check if a column uses ID-based lookup options (e.g., status, type)
   */
  isLookupField(column: GridColumn): boolean {
    return !!column.lookupOptions && column.lookupOptions.length > 0;
  }

  /**
   * Resolve a lookup ID to its display name
   */
  /**
   * What a multi-value filter's dropdown button says: the chosen names — "Cancelled",
   * "Confirmed, Cancelled", "Victoria +2" — rather than "N selected" beside a second list of the
   * same values. `full` is every name, for the button's title.
   */
  multiValueSummary(filter: FilterItem, kind: 'user' | 'lookup' | 'plain', full = false): string {
    const values = filter.multiValues ?? [];
    const label = (v: string): string =>
      kind === 'user' ? this.getUserNameById(v) : kind === 'lookup' ? this.getLookupLabel(filter.column, v) : String(v);
    if (full || values.length <= 2) return values.map(label).join(', ');
    return `${label(values[0])} +${values.length - 1}`;
  }

  getLookupLabel(column: GridColumn, id: string | number): string {
    if (!column.lookupOptions) return String(id);
    const option = column.lookupOptions.find(o => String(o.id) === String(id));
    return option?.name || String(id);
  }

  isStateField(column: GridColumn): boolean {
    return column.field?.toLowerCase() === 'state';
  }

  getFilteredLookupOptions(column: GridColumn): { id: string | number; name: string }[] {
    return this.visibleOptions(
      column.lookupOptions ?? SavedViewsComponent.NO_OPTIONS,
      (option) => option.name
    );
  }

  getFilteredDropdownOptions(column: GridColumn, isState: boolean): string[] {
    const options = isState ? this.stateOptions : (column.options ?? SavedViewsComponent.NO_OPTIONS);
    return this.visibleOptions(options, (option) => option);
  }

  isTagsField(column: GridColumn): boolean {
    return column.field?.toLowerCase() === 'tags';
  }

  isWorkPackageModule(): boolean {
    return this.moduleType === 'work-package';
  }

  isMilestoneColumn(column: GridColumn): boolean {
    return this.isWorkPackageModule() && column.columnType === ColumnType.MILESTONE;
  }

  isMilestoneNameColumn(column: GridColumn): boolean {
    return column.field?.toLowerCase() === 'milestonename';
  }

  shouldShowMilestoneSelector(column: GridColumn): boolean {
    return this.isMilestoneColumn(column) && !this.isMilestoneNameColumn(column);
  }

  getMilestoneName(milestoneId: number): string {
    return this.milestoneOptions.find(m => m.id === milestoneId)?.name ?? String(milestoneId);
  }

  private resolveSharedMilestoneId(rules: ViewFilterRule[]): number {
    // Newest format: numeric ID stored as value
    const savedId = this.viewsFilterService.extractSharedMilestoneId(rules);
    if (savedId) {
      return savedId;
    }

    // Old format: name string stored as value — resolve via options (if loaded)
    const milestoneRule = rules.find(
      (r) => r.type === ColumnType.MILESTONE
    );

    if (milestoneRule?.field === 'milestoneName' && milestoneRule?.value && this.milestoneOptions.length) {
      const match = this.milestoneOptions.find(
        m => m.name === milestoneRule.value
      );

      if (match) {
        return match.id;
      }
    }

    // Oldest format: milestoneId as a direct property on the rule
    if (milestoneRule?.milestoneId != null) {
      return Number(milestoneRule.milestoneId);
    }

    return 0;
  }

  updateSharedMilestoneId(id: number): void {
    if (!this.isWorkPackageModule()) return;
    this.sharedMilestoneId = id;
    this.notifyFiltersChanged();
  }

  loadTagOptions(): void {
    if (this.tagsLoaded || this.tagsLoading) return;
    const prefs = this.userPreferencesService.getUserPreferencesData();
    const projectId = prefs?.selectedProjectId ?? 0;
    const companyId = String(prefs?.selectedCompanyId ?? '');
    if (!projectId || !companyId) return;
    this.tagsLoading = true;
    // Tag managers keep the full project list from `tags/config`, as before. Anyone else gets a 403
    // (and its toast) from that admin endpoint, so they get the module's own tags instead — the
    // catalog key its records are tagged under, which the run API filters on.
    const moduleKey = this.isTagManager() ? undefined : SavedViewsComponent.TAG_MODULE_KEYS[this.moduleType];
    const tags$ = moduleKey
      ? this.centralizedTagsService.getAvailableTags(projectId, companyId, moduleKey).pipe(
          map((rows: { tag?: { id: string; name: string } }[]) => (rows ?? []).map(row => row.tag).filter(t => !!t?.name)),
        )
      : this.centralizedTagsService.getConfig(projectId, companyId).pipe(
          map(config => (config.tags || []).filter(t => t.status === StatusID.Active)),
        );
    this.subs.add(
      tags$.pipe(
        // A 403 completes without emitting (the auth interceptor swallows it) — still stop loading.
        finalize(() => { this.tagsLoading = false; }),
      ).subscribe({
        next: (tags) => {
          // One option per name — the run API matches tags by name.
          this.tagOptions = [...new Map(tags.map(t => [t.name, { id: t.id, name: t.name }])).values()];
          this.tagsLoaded = true;
        },
        error: () => { /* the picker stays empty; finalize ends the loading state */ },
      })
    );
  }

  /**
   * Mirrors the API's tag-manager rule (centralizedTagsController.hasTagManagePermission): an
   * internal user with edit or delete on the Tags menu.
   */
  private isTagManager(): boolean {
    if (Number(this.localStorageService.getItem('partnerCompanyId') ?? 0) > 0) return false;
    return this.permissionService.canByName('Tags', 'edit') || this.permissionService.canByName('Tags', 'delete');
  }

  /** Tag catalog key (tblTagModuleCatalog) each module's records are tagged under. */
  private static readonly TAG_MODULE_KEYS: Partial<Record<string, string>> = {
    'work-order': 'work_order',
    'work-package': 'work_package',
    action: 'actions',
    // An inspection carries its template's tags.
    inspection: 'templates',
  };

  getFilteredTagOptions(): { id: string; name: string }[] {
    return this.visibleOptions(this.tagOptions, (tag) => tag.name);
  }

  getTagLabel(id: string): string {
    return this.tagOptions.find(t => t.id === id)?.name || id;
  }

  /**
   * Add a new filter with the default operator for the column's data type
   */
  addFilter(column: GridColumn): void {
    if (this.isTagsField(column)) {
      this.loadTagOptions();
    }
    const defaultOp = this.viewsFilterService.getDefaultOperatorForColumn(
      column,
      this.viewsFilterService.isUserField(column),
    );
    const filterItem: FilterItem = {
      column,
      operator: defaultOp,
      value: '',
      valueTo: '',
      multiValues: [],
    };
    // For milestone columns, pre-select the first milestone if none set yet
    if (this.shouldShowMilestoneSelector(column) && !this.sharedMilestoneId && this.milestoneOptions.length > 0) {
      this.sharedMilestoneId = this.milestoneOptions[0].id;
    }
    this.appliedFilters.push(filterItem);
    this.closeFilterMenu();
  }

  /**
   * Remove a filter by index
   */
  removeFilter(index: number): void {
    this.appliedFilters.splice(index, 1);
    this.notifyFiltersChanged();
  }

  /**
   * Update filter operator - resets values when operator type changes
   */
  updateFilterOperator(index: number, operator: string): void {
    if (this.appliedFilters[index]) {
      this.appliedFilters[index].operator = operator;
      this.appliedFilters[index].value = '';
      this.appliedFilters[index].valueTo = '';
      this.appliedFilters[index].multiValues = [];
      this.notifyFiltersChanged();
    }
  }

  /**
   * Update filter value with debounce (single value input)
   */
  updateFilterValue(index: number, value: string): void {
    if (this.filterValueDebounceTimer) {
      clearTimeout(this.filterValueDebounceTimer);
    }

    if (this.appliedFilters[index]) {
      this.appliedFilters[index].value = value;
    }

    this.filterValueDebounceTimer = setTimeout(() => {
      this.notifyFiltersChanged();
    }, this.DEBOUNCE_TIME);
  }

  /**
   * Update the 'valueTo' field for 'between' operator (debounced)
   */
  updateFilterValueTo(index: number, value: string): void {
    if (this.filterValueDebounceTimer) {
      clearTimeout(this.filterValueDebounceTimer);
    }

    if (this.appliedFilters[index]) {
      this.appliedFilters[index].valueTo = value;
    }

    this.filterValueDebounceTimer = setTimeout(() => {
      this.notifyFiltersChanged();
    }, this.DEBOUNCE_TIME);
  }


  /**
   * Add a value to multi-value filter (in/notin). Triggered on Enter key.
   */
  addMultiValue(index: number, value: string, event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    const trimmed = value?.trim();
    if (!trimmed || !this.appliedFilters[index]) {
      return;
    }

    if (!this.appliedFilters[index].multiValues) {
      this.appliedFilters[index].multiValues = [];
    }

    if (!this.appliedFilters[index].multiValues!.includes(trimmed)) {
      this.appliedFilters[index].multiValues!.push(trimmed);
    }

    if (event?.target) {
      (event.target as HTMLInputElement).value = '';
    }
    this.notifyFiltersChanged();
  }

  /**
   * Remove a value from multi-value filter
   */
  removeMultiValue(filterIndex: number, valueIndex: number): void {
    if (this.appliedFilters[filterIndex]?.multiValues) {
      this.appliedFilters[filterIndex].multiValues!.splice(valueIndex, 1);
      this.notifyFiltersChanged();
    }
  }

  /**
   * Handle dropdown multi-select change for dropdown-type columns
   */
  updateMultiSelectValues(index: number, values: string[]): void {
    if (this.appliedFilters[index]) {
      this.appliedFilters[index].multiValues = values;
      this.notifyFiltersChanged();
    }
  }

  /**
   * Extract selected values from a multi-select element
   */
  getSelectedOptions(event: Event): string[] {
    const selectElement = event.target as HTMLSelectElement;
    const selectedOptions = Array.from(selectElement.selectedOptions);
    return selectedOptions.map((option) => option.value);
  }

  /**
   * Check if any filters are applied
   */
  get hasAppliedFilters(): boolean {
    return this.appliedFilters.length > 0;
  }

  /** Whether the host passed any grid tools to trail on the filter row. */
  get hasGridTools(): boolean {
    return !!this.searchControl || this.columnConfig.length > 0 || !!this.gridToolsExtra;
  }

  /** The tools go at the end of the views row when there is one (full page, views on)… */
  get gridToolsInViewsRow(): boolean {
    return this.hasGridTools && this.enableViews && !this.compactMode;
  }

  /** …and on the filter row otherwise (views off: the EOI register, Contacts). */
  get gridToolsInFilterRow(): boolean {
    return this.hasGridTools && !this.gridToolsInViewsRow;
  }

  /**
   * Clear all applied filters
   */
  clearAllFilters(): void {
    this.appliedFilters = [];
    this.filterLogic = 'AND';
    this.notifyFiltersChanged();
  }

  /**
   * Puts a filter into the row from outside, as though the user had built it there.
   *
   * How a drill-through hands its narrowing over: the arriving screen gets the same removable chip
   * it would have got by picking the column by hand — visible, editable, and cleared by the same
   * Clear all — rather than a hidden condition on the query that nobody can see or undo.
   *
   * Replaces whatever the row is showing, so it is for arrival, not for adding to a filter set the
   * user has already built. Emits, which is what makes the grid reload against it.
   */
  applyExternalFilters(payload: ViewFiltersPayload): void {
    this.appliedFilters = this.viewsFilterService.filtersFromRules(
      payload?.rules ?? [],
      this.availableColumns,
    );
    this.filterLogic = payload?.logic ?? 'AND';
    this.emitFiltersChanged();
  }

  /**
   * Build FilterSelection[] from applied filters and emit.
   */
  private emitFiltersChanged(): void {
    if (this._suppressFilterEmit) return;

    const selections: FilterSelection[] = [];

    for (const filter of this.appliedFilters) {
      const dataType = filter.column.dataType || 'string';
      const operator = filter.operator;
      let value: string | number | (string | number)[] | null;

      if (isNoValueOperator(operator)) {
        value = null;
      } else if (isBetweenOperator(operator)) {
        if (!filter.value && !filter.valueTo) continue;
        value = [filter.value || '', filter.valueTo || ''];
      } else if (isMultiValueOperator(operator)) {
        if (!filter.multiValues?.length) continue;
        value = [...filter.multiValues];
      } else {
        if (!filter.value) continue;
        value = filter.value;
      }

      selections.push({
        fieldKey: filter.column.field,
        dataType,
        operator,
        value,
      });
    }

    this.filtersChanged.emit({
      filters: selections,
      logic: this.filterLogic,
      payload: this.getFiltersAsPayload(),
      isDirty: this.isDirty,
    });
  }

  // ========== FILTER CHANGE → EMIT IMMEDIATELY (no auto-save) ==========

  /**
   * Called after any filter mutation. Emits filtersChanged immediately so the
   * grid re-fetches data with the new filters. Does NOT save to the DB — the user
   * must press the explicit Save button for that.
   *
   * For system views (Default/Archive/Draft) and the default view, persists to
   * localStorage as before.
   */
  private notifyFiltersChanged(): void {
    // Persist default view filters in session storage so they survive page refresh
    if (this.activeView && this.isDefaultView(this.activeView)) {
      this.saveDefaultViewFiltersToSession();
    }
    // Mark dirty for named views so the action bar becomes visible
    if (this.activeView && !this.isSystemView(this.activeView)) {
      this._markDirtyIfChanged();
    }
    this.emitFiltersChanged();
  }

  /**
   * Build view payload for save operation.
   * For work-order module, always prepends default filters (parentWorkOrderId, recordState)
   * so they are persisted server-side regardless of user-applied filters.
   */
  private buildViewPayload(): ViewDefinitionPayload {
    const prefs = this.userPreferencesService.getUserPreferencesData();
    let filtersPayload = this.getFiltersAsPayload();

    if (this.moduleType === 'work-order') {
      const defaultRules: ViewFilterRule[] = [
        { type: ColumnType.STANDARD, field: 'parentWorkOrderId', op: FilterOperator.EQUALS, value: '0' },
        { type: ColumnType.STANDARD, field: 'recordState', op: FilterOperator.EQUALS, value: RecordState.Active },
      ];
      filtersPayload = {
        ...filtersPayload,
        rules: [...defaultRules, ...filtersPayload.rules],
      };
    }

    return {
      id: this.activeView.id,
      companyId: prefs?.selectedCompanyId ?? 0,
      projectId: prefs?.selectedProjectId ?? 0,
      name: this.activeView.name,
      description: this.activeView.description ?? '',
      visibility: this.activeView.visibility ?? ViewVisibility.Private,
      columns: this.activeView.columns ?? { standard: [], custom: [], milestone: [] },
      filters: filtersPayload,
      sort: this.activeView.sort,
      createdBy: this.activeView.ownerId,
    };
  }
}
